import axios from 'axios'
import { faro } from '@grafana/faro-web-sdk'
import { getBaseUrl, REFRESH_ENDPOINT } from '../constant'
import { decodeJwt, getRequestHeaders } from '../utils'

const extractError = (method, error) => {
  const reason =
    error?.response?.data?.error?.message ||
    `Error in ${method.toUpperCase()} call`

  const code = (error?.response?.data?.error?.code || 'UNKNOWN')
    .replace(/\s+/g, '_')
    .toUpperCase()

  const status = error?.response?.status || 0

  const err = new Error(reason)
  err.status = status
  err.code = code

  return err
}

export const isTokenValid = (token) => {
  try {
    // @ts-ignore
    const { exp } = decodeJwt(token)
    const currentTime = Math.floor(Date.now() / 1000)
    return exp && exp > currentTime
  } catch {
    return false
  }
}

export class Fetcher {
  static instance

  static isRefreshing = false

  static getInstance() {
    if (this.instance !== undefined) return this.instance
    this.instance = new Fetcher()
    return this.instance
  }

  refreshToken = async () => {
    if (Fetcher.isRefreshing) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!Fetcher.isRefreshing) {
            clearInterval(interval)
            resolve()
          }
        }, 100)
      })
    }

    Fetcher.isRefreshing = true
    const refreshToken = JSON.parse(localStorage.getItem('tokenStorage'))
    const reqData = {
      refreshToken: refreshToken?.refreshToken,
      tenantID: refreshToken?.tenants[0]?.id,
    }

    try {
      const response = await axios({
        method: 'post',
        url: `${getBaseUrl() + REFRESH_ENDPOINT}`,
        data: reqData,
        headers: { 'Content-Type': 'application/json' },
      })

      const result = JSON.parse(localStorage?.getItem('tokenStorage'))
      result.accessToken = response?.data?.data?.accessToken
      localStorage.setItem('tokenStorage', JSON.stringify(result))

      Fetcher.isRefreshing = false
    } catch (error) {
      localStorage.clear()
      window.location.href = '/login'
      throw error
    }
    return null
  }

  /**
   * Common request handler with token check + retry logic
   */
  sendRequest = async (method, url, reqData = null, ct = 1) => {
    try {
      // ✅ Ensure valid token before request
      const tokenStorage = JSON.parse(localStorage.getItem('tokenStorage'))
      const accessToken = tokenStorage?.accessToken
      if (!accessToken || !isTokenValid(accessToken)) {
        await this.refreshToken()
      }

      const response = await axios({
        method,
        url,
        data: reqData,
        headers: getRequestHeaders(),
      })
      return response
    } catch (error) {
      // Retry once if unauthorized
      if (error?.response?.status === 401 && ct === 1) {
        await this.refreshToken()
        return this.sendRequest(method, url, reqData, ct + 1)
      }

      if (faro?.api?.pushError) {
        faro.api.pushError(error)
      }

      throw extractError(method, error)
    }
  }

  // convenience methods
  getData = (url) => this.sendRequest('get', url)

  postData = (url, reqData) => this.sendRequest('post', url, reqData)

  putData = (url, reqData) => this.sendRequest('put', url, reqData)

  patchData = (url, reqData) => this.sendRequest('patch', url, reqData)

  deleteData = (url) => this.sendRequest('delete', url)
}

export const createFetcher = () => Fetcher.getInstance()
